<template>
  <div class="offline-process-container">
    <!-- 头部区域：诊断主标题、对齐整站分析的呼吸灯标签、右侧进度计数 -->
    <div class="section-title-row d-flex align-center justify-space-between mb-4">
      <div class="d-flex align-center flex-wrap ga-3">
        <h4 class="process-main-title mb-0">
          {{ isStationOffline ? '正在进行整站离线分析' : '正在进行单机离线分析' }}
        </h4>
        <Transition name="phase-status" mode="out-in">
          <span :key="phase" class="phase-label" :class="`phase-${phase}`">
            <span class="phase-dot" aria-hidden="true"></span>
            {{ phaseText }}
          </span>
        </Transition>
      </div>

      <div class="step-progress-counter font-mono">
        已完成 {{ completedStepsCount }}/{{ steps.length }} 步骤
      </div>
    </div>

    <!-- 过程副标题描述 -->
    <div class="process-sub-desc text-caption text-slate-500 mb-4">
      正在调取运行遥测数据、排查同站设备关联状态与通信中断原因
    </div>

    <!-- 核心步骤列表：完全遵循整站分析与告警分析的标准规范 -->
    <ol class="process-steps-live">
      <li
        v-for="step in steps"
        :key="step.id"
        :class="`step-${step.status}`"
      >
        <span class="step-icon">
          <span v-if="step.status === 'completed'" class="icon-check">✓</span>
          <span v-else-if="step.status === 'failed'" class="icon-failed">!</span>
          <span v-else-if="step.status === 'started'" class="icon-loading"></span>
          <span v-else class="icon-pending">•</span>
        </span>
        <span class="step-text">{{ step.text }}</span>
        <span v-if="getDisplayStatus(step)" class="step-status">{{ getDisplayStatus(step) }}</span>
      </li>
    </ol>

    <!-- 辅助工具区：现场排查依据与参考数据 -->
    <div v-if="toolItems.length" class="tool-list">
      <div class="tool-list-title d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center ga-2">
          <v-icon size="16" color="#3A68FF">mdi-database-search-outline</v-icon>
          <span class="font-weight-bold text-slate-800">现场排查依据与数据支撑</span>
        </div>
        <span class="tool-count-badge font-mono">
          {{ completedToolCount }}/{{ toolItems.length }} 项就绪
        </span>
      </div>

      <div class="tool-items">
        <div
          v-for="tool in toolItems"
          :key="tool.id"
          class="tool-item"
          :class="`tool-state-${tool.status}`"
        >
          <div class="tool-icon-col">
            <span v-if="tool.status === 'started'" class="tool-icon-loading"></span>
            <v-icon
              v-else
              size="16"
              :color="tool.status === 'failed' ? '#F5222D' : tool.status === 'completed' ? '#52C41A' : '#94a3b8'"
            >
              {{ toolStatusIcon(tool.status) }}
            </v-icon>
          </div>

          <div class="tool-copy">
            <span class="tool-name">{{ tool.label }}</span>
            <span class="tool-description">
              {{ tool.error || tool.description }}
            </span>
          </div>

          <span class="tool-status" :class="`tool-${tool.status}`">
            {{ toolStatusText(tool.status) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  steps: {
    type: Array,
    default: () => []
  },
  tools: {
    type: Object,
    default: () => ({})
  },
  isStationOffline: {
    type: Boolean,
    default: false
  },
  phase: {
    type: String,
    default: 'running'
  }
})

const phaseText = computed(() => {
  switch (props.phase) {
    case 'loading-context':
      return '正在准备分析'
    case 'connecting':
      return '正在连接智能体'
    case 'running':
      return '正在分析'
    case 'success':
      return '分析已完成'
    case 'failed':
      return '分析异常'
    default:
      return '正在分析'
  }
})

const completedStepsCount = computed(() => {
  return props.steps.filter(s => s.status === 'completed').length
})

const toolItems = computed(() => Object.values(props.tools || {}))
const completedToolCount = computed(() => toolItems.value.filter(t => t.status === 'completed').length)

function getDisplayStatus(step) {
  if (step.statusDisplay) return step.statusDisplay
  if (step.status === 'completed') return '已完成'
  if (step.status === 'started') return '分析中...'
  if (step.status === 'failed') return '异常'
  return ''
}

function toolStatusIcon(status) {
  if (status === 'completed') return 'mdi-check-circle-outline'
  if (status === 'failed') return 'mdi-alert-circle-outline'
  return 'mdi-clock-outline'
}

function toolStatusText(status) {
  if (status === 'completed') return '已就绪'
  if (status === 'failed') return '异常'
  if (status === 'started') return '检索中...'
  return '等待调度'
}
</script>

<style scoped lang="scss">
.offline-process-container {
  padding: 22px 24px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);

  /* 标题与呼吸灯 */
  .section-title-row {
    .process-main-title {
      font-size: 15px;
      font-weight: 700;
      color: #0f172a;
      line-height: 1.4;
    }

    .phase-label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 0;
      padding: 4px 10px;
      border-radius: 999px;
      background: rgba(58, 104, 255, 0.08);
      color: #3A68FF;
      font-size: 12px;
      font-weight: 600;

      .phase-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
      }

      &.phase-success {
        background: rgba(82, 196, 26, 0.1);
        color: #52C41A;
      }

      &.phase-failed {
        background: rgba(245, 34, 45, 0.08);
        color: #F5222D;
      }

      &.phase-loading-context .phase-dot,
      &.phase-connecting .phase-dot,
      &.phase-running .phase-dot {
        animation: phase-dot-pulse 1.35s ease-in-out infinite;
      }
    }

    .step-progress-counter {
      font-size: 12px;
      font-weight: 600;
      color: #3A68FF;
      background: rgba(58, 104, 255, 0.06);
      padding: 3px 10px;
      border-radius: 6px;
    }
  }

  .process-sub-desc {
    line-height: 1.4;
  }

  /* 核心步骤列表（整站分析 1:1 标准规范，加载圈清晰锐利） */
  .process-steps-live {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 0;
      font-size: 14px;
      transition: all 0.25s ease;
      border-bottom: 1px dashed rgba(0, 0, 0, 0.035);

      &:last-child {
        border-bottom: none;
      }

      .step-icon {
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: 50%;
        font-size: 12px;
      }

      .icon-check {
        color: #52C41A;
        font-weight: 700;
      }

      /* 旋转 loading 圈圈：加粗纯正科技蓝，高速转动，极高辨识度 */
      .icon-loading {
        width: 14px;
        height: 14px;
        border: 2.2px solid #3A68FF;
        border-top-color: transparent;
        border-radius: 50%;
        animation: dialog-spin 0.75s linear infinite;
      }

      .icon-pending {
        color: rgba(46, 46, 46, 0.2);
        font-size: 11px;
      }

      .icon-failed {
        color: #F5222D;
        font-weight: 700;
      }

      .step-text {
        flex: 1;
        line-height: 1.45;
      }

      .step-status {
        color: rgba(46, 46, 46, 0.45);
        font-size: 12px;
        font-weight: 500;
      }

      &.step-completed {
        color: #1e293b;

        .step-icon {
          background: rgba(82, 196, 26, 0.1);
        }

        .step-status {
          color: #52C41A;
          font-weight: 600;
        }
      }

      &.step-started {
        color: #3A68FF;
        font-weight: 600;

        .step-icon {
          background: rgba(58, 104, 255, 0.08);
        }

        .step-status {
          color: #3A68FF;
          font-weight: 600;
        }
      }

      &.step-pending {
        color: rgba(71, 85, 105, 0.65);
      }

      &.step-failed {
        color: #F5222D;

        .step-icon {
          background: rgba(245, 34, 45, 0.08);
        }

        .step-status {
          color: #F5222D;
          font-weight: 600;
        }
      }
    }
  }

  /* 辅助工具 / 数据库底座与检索依据 */
  .tool-list {
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);

    .tool-list-title {
      color: rgba(30, 41, 59, 0.85);
      font-size: 14px;

      .tool-count-badge {
        font-size: 12px;
        color: #3A68FF;
        background: rgba(58, 104, 255, 0.06);
        padding: 2px 8px;
        border-radius: 4px;
        font-weight: 600;
      }
    }

    .tool-items {
      display: flex;
      flex-direction: column;
      gap: 9px;
    }

    .tool-item {
      display: grid;
      grid-template-columns: 20px minmax(0, 1fr) auto;
      align-items: center;
      min-height: 40px;
      gap: 12px;
      padding: 8px 14px;
      border-radius: 8px;
      background: #f8fafc;
      border: 1px solid #f1f5f9;
      color: rgba(46, 46, 46, 0.75);
      font-size: 12px;
      transition: all 0.2s ease;

      &:hover {
        background: #f1f5f9;
        border-color: #e2e8f0;
      }

      &.tool-state-started {
        background: #f0f7ff;
        border-color: #dbeafe;
      }

      &.tool-state-completed {
        background: #fcfdfc;
        border-color: #f1f5f9;
      }

      .tool-icon-col {
        display: flex;
        align-items: center;
        justify-content: center;

        .tool-icon-loading {
          width: 13px;
          height: 13px;
          border: 2px solid #3A68FF;
          border-top-color: transparent;
          border-radius: 50%;
          animation: dialog-spin 0.75s linear infinite;
        }
      }

      .tool-copy {
        min-width: 0;

        .tool-name {
          display: block;
          color: #1e293b;
          font-weight: 600;
          font-size: 14px;
          line-height: 1.4;
        }

        .tool-description {
          display: block;
          min-width: 0;
          margin-top: 2px;
          color: #64748b;
          font-size: 11px;
          line-height: 1.35;
        }
      }

      .tool-status {
        flex-shrink: 0;
        font-size: 12px;
        font-weight: 600;

        &.tool-completed {
          color: #52C41A;
        }
        &.tool-started {
          color: #3A68FF;
        }
        &.tool-pending {
          color: #94a3b8;
        }
        &.tool-failed {
          color: #F5222D;
        }
      }
    }
  }
}

@keyframes dialog-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes phase-dot-pulse {
  0%, 100% {
    opacity: 0.45;
    transform: scale(0.82);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
    box-shadow: 0 0 10px rgba(58, 104, 255, 0.7);
  }
}
</style>
